import React, { useMemo, useState } from 'react'
import { Header, Message, Input, Dropdown, Icon } from 'semantic-ui-react'
import { LoadingWrapper } from '../common'
import { SummaryCard, FilterPill } from '../common/DashboardWidgets'
import { TeamPanel } from './TeamPanel'
import { useManagerDashboard } from './useManagerDashboard'
import './ManagerDashboard.css'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatMarketName = market =>
  (market || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

// ─── ManagerDashboard ─────────────────────────────────────────────────────────

export const ManagerDashboard = () => {
  const { overall, teams, loading, error } = useManagerDashboard()
  const [search, setSearch] = useState('')
  const [marketFilter, setMarketFilter] = useState('')
  const [readinessFilter, setReadinessFilter] = useState('all')
  const [activeFilter, setActiveFilter] = useState('active')
  const [groupByMarket, setGroupByMarket] = useState(false)

  // ── Market dropdown options ───────────────────────────────────────────────
  const marketOptions = useMemo(() => {
    const markets = [...new Set(teams.map(t => t.market).filter(Boolean))].sort()
    return [
      { key: '', value: '', text: 'All Markets' },
      ...markets.map(m => ({ key: m, value: m, text: formatMarketName(m) })),
    ]
  }, [teams])

  // ── Filtered teams ────────────────────────────────────────────────────────
  const filteredTeams = useMemo(() => teams.filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false
    if (marketFilter && t.market !== marketFilter) return false
    if (readinessFilter === 'ready' && t.latestReadiness?.status !== 'ready') return false
    if (readinessFilter === 'not_ready' && (!t.latestReadiness || t.latestReadiness.status === 'ready')) return false
    if (readinessFilter === 'none' && t.latestReadiness != null) return false
    if (activeFilter === 'active' && !t.isActive) return false
    if (activeFilter === 'inactive' && t.isActive) return false
    return true
  }), [teams, search, marketFilter, readinessFilter, activeFilter])

  // ── Grouped by market ─────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    if (!groupByMarket) return null
    const groups = {}
    filteredTeams.forEach(t => {
      const key = t.market || 'Unknown'
      if (!groups[key]) groups[key] = []
      groups[key].push(t)
    })
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredTeams, groupByMarket])

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mgr-dashboard">
      <Header as="h2">Manager Dashboard</Header>
      <LoadingWrapper loading={loading}>
        {error && (
          <Message negative>
            <Message.Header>Error loading dashboard</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {!error && (
          <>
            {/* ── Summary cards ── */}
            <div className="mgr-summary-row">
              <SummaryCard
                label="Team Readiness"
                value={`${overall?.teamsReady ?? 0}/${overall?.teamsActive ?? 0}`}
                sub="ready / active teams"
                icon="users"
                color={(overall?.teamsReady ?? 0) < (overall?.teamsActive ?? 0) ? '#e53935' : '#43a047'}
                alert={(overall?.teamsReady ?? 0) < (overall?.teamsActive ?? 0)}
              />
              <SummaryCard
                label="Members"
                value={overall?.membersTotal ?? 0}
                sub={(overall?.membersSick ?? 0) > 0 ? `${overall.membersSick} sick` : 'all active'}
                icon="user"
                color={(overall?.membersSick ?? 0) > 0 ? '#e53935' : undefined}
                alert={(overall?.membersSick ?? 0) > 0}
              />
              <SummaryCard
                label="Asset Health"
                value={`${overall?.assetsFunctional ?? 0}/${overall?.assetsTotal ?? 0}`}
                sub="functional"
                icon="truck"
                color={(overall?.assetsFunctional ?? 0) < (overall?.assetsTotal ?? 0) ? '#f57f17' : '#43a047'}
                alert={(overall?.assetsFunctional ?? 0) < (overall?.assetsTotal ?? 0)}
              />
              <SummaryCard
                label="Open Problems"
                value={overall?.openProblems ?? 0}
                sub="asset issues"
                icon="wrench"
                color={(overall?.openProblems ?? 0) > 0 ? '#e65100' : undefined}
                alert={(overall?.openProblems ?? 0) > 0}
              />
              <SummaryCard
                label="Open Roadblocks"
                value={overall?.openRoadblocks ?? 0}
                sub="roadblocks"
                icon="warning sign"
                color={(overall?.openRoadblocks ?? 0) > 0 ? '#f57f17' : undefined}
                alert={(overall?.openRoadblocks ?? 0) > 0}
              />
              {(overall?.pendingMoves ?? 0) > 0 && (
                <SummaryCard
                  label="Pending Moves"
                  value={overall.pendingMoves}
                  sub="scheduled asset moves"
                  icon="arrows alternate"
                  color="#1565c0"
                />
              )}
            </div>

            {/* ── Filter bar ── */}
            <div className="mgr-filter-bar">
              {/* Row 1: search + market + group-by */}
              <div className="mgr-filter-bar__top">
                <Input
                  icon="search"
                  placeholder="Search teams..."
                  value={search}
                  onChange={(_, { value }) => setSearch(value)}
                  size="large"
                />
                <Dropdown
                  selection
                  search
                  options={marketOptions}
                  value={marketFilter}
                  onChange={(_, { value }) => setMarketFilter(value)}
                  placeholder="All Markets"
                  style={{ minWidth: '150px' }}
                />
                <div style={{ marginLeft: 'auto' }}>
                  <FilterPill
                    label={groupByMarket ? '✓ Grouped by Market' : 'Group by Market'}
                    active={groupByMarket}
                    onClick={() => setGroupByMarket(g => !g)}
                  />
                </div>
              </div>

              {/* Row 2: quick filter pills */}
              <div className="mgr-filter-bar__pills-row">
                <div className="mgr-filter-group">
                  <span className="mgr-filter-label">Status:</span>
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'active', label: 'Active' },
                    { key: 'inactive', label: 'Inactive' },
                  ].map(f => (
                    <FilterPill key={f.key} label={f.label} active={activeFilter === f.key} onClick={() => setActiveFilter(f.key)} />
                  ))}
                </div>
                <div className="mgr-filter-group">
                  <span className="mgr-filter-label">Readiness:</span>
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'ready', label: 'Ready' },
                    { key: 'not_ready', label: 'Not Ready' },
                    { key: 'none', label: 'None' },
                  ].map(f => (
                    <FilterPill key={f.key} label={f.label} active={readinessFilter === f.key} onClick={() => setReadinessFilter(f.key)} />
                  ))}
                </div>
              </div>
            </div>

            {/* ── Results count ── */}
            <div className="mgr-results-count">
              Showing {filteredTeams.length} of {teams.length} team{teams.length !== 1 ? 's' : ''}
            </div>

            {/* ── Team list ── */}
            {teams.length === 0 ? (
              <Message info>
                <Message.Header>No teams found</Message.Header>
                <p>There are no teams available for your account.</p>
              </Message>
            ) : filteredTeams.length === 0 ? (
              <Message info>
                <Message.Header>No teams match the current filters</Message.Header>
                <p>Try adjusting your search or filter criteria.</p>
              </Message>
            ) : grouped ? (
              grouped.map(([market, mTeams]) => (
                <div key={market} className="mgr-market-group">
                  <div className="mgr-market-group__header">
                    <Icon name="map marker alternate" color="grey" />
                    {formatMarketName(market)} ({mTeams.length})
                  </div>
                  {mTeams.map(team => <TeamPanel key={team.id} team={team} />)}
                </div>
              ))
            ) : (
              filteredTeams.map(team => <TeamPanel key={team.id} team={team} />)
            )}
          </>
        )}
      </LoadingWrapper>
    </div>
  )
}

